import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';

interface Step {
  label: string;
  delay: number; // ms after start to activate this step
}

interface StepProgressProps {
  steps: Step[];
  active: boolean; // when true, starts the timer
}

/**
 * Simulated step progress indicator.
 * Steps light up sequentially on a timer when `active` is true.
 * Resets when `active` becomes false.
 */
export default function StepProgress({ steps, active }: StepProgressProps) {
  const [currentStep, setCurrentStep] = useState(-1);

  useEffect(() => {
    if (!active) {
      setCurrentStep(-1);
      return;
    }

    // Activate step 0 immediately
    setCurrentStep(0);

    const timers = steps.slice(1).map((step, idx) =>
      setTimeout(() => setCurrentStep(idx + 1), step.delay)
    );

    return () => timers.forEach(clearTimeout);
  }, [active, steps]);

  if (!active) return null;

  // Progress bar percentage: smoothly interpolate between steps
  const progressPercent = Math.min(
    Math.round(((currentStep + 1) / steps.length) * 100),
    95 // never show 100% until truly done
  );

  return (
    <div className="mt-4">
      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
        <div
          className="bg-primary-600 h-2 rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <p className="text-xs text-gray-400 text-right mb-3">{progressPercent}%</p>

      {/* Step list */}
      <div className="space-y-2">
      {steps.map((step, idx) => {
        const isComplete = idx < currentStep;
        const isCurrent = idx === currentStep;
        const isPending = idx > currentStep;

        return (
          <div
            key={idx}
            className={`flex items-center space-x-3 transition-opacity duration-300 ${
              isPending ? 'opacity-30' : 'opacity-100'
            }`}
          >
            {isComplete ? (
              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
            ) : isCurrent ? (
              <Loader2 className="h-4 w-4 text-primary-600 animate-spin flex-shrink-0" />
            ) : (
              <div className="h-4 w-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
            )}
            <span className={`text-sm ${
              isComplete ? 'text-green-700' :
              isCurrent ? 'text-primary-700 font-medium' :
              'text-gray-400'
            }`}>
              {step.label}
            </span>
          </div>
        );
      })}
      </div>
    </div>
  );
}
